import React, { useMemo } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { CssBaseline, GlobalStyles } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { BARCODE_TOOLS, QR_TOOLS } from "./data/tools";
import { THEME_KEY } from "./constants/storageKeys";
import AppLayout from "./components/layout/AppLayout";
import ToolsHome from "./components/pages/ToolsHome";
import ToolsGroupPage from "./components/pages/ToolsGroupPage";
import QRToolPage from "./components/pages/QRToolPage";
import BarcodeToolPage from "./components/pages/BarcodeToolPage";
import { useLocalStorageState } from "./utils/storage";

function App() {
    const [themeMode, setThemeMode] = useLocalStorageState(THEME_KEY, "light");

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: themeMode,
                    primary: { main: "#ff6b35" },
                    secondary: { main: "#1f8a70" },
                    background: {
                        default: themeMode === "dark" ? "#0e0f12" : "#f6f3ed",
                        paper: themeMode === "dark" ? "#1b1d22" : "#ffffff",
                    },
                },
                shape: { borderRadius: 16 },
                typography: {
                    fontFamily:
                        '"Space Grotesk", system-ui, -apple-system, sans-serif',
                },
            }),
        [themeMode],
    );

    const toggleTheme = () => {
        setThemeMode((current) => (current === "dark" ? "light" : "dark"));
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles
                styles={{
                    body: {
                        background:
                            themeMode === "dark"
                                ? "radial-gradient(circle at top, rgba(255,107,53,0.12), transparent 50%), radial-gradient(circle at 15% 80%, rgba(31,138,112,0.16), transparent 55%), #0e0f12"
                                : "radial-gradient(circle at top, rgba(255,107,53,0.08), transparent 50%), radial-gradient(circle at 20% 80%, rgba(31,138,112,0.12), transparent 55%), #f6f3ed",
                    },
                    "*": { boxSizing: "border-box" },
                }}
            />
            <BrowserRouter>
                <AppLayout themeMode={themeMode} onToggleTheme={toggleTheme}>
                    <Routes>
                        <Route path="/" element={<ToolsHome />} />
                        <Route
                            path="/qr"
                            element={
                                <ToolsGroupPage
                                    title="QR Generator Tools"
                                    description="Pick a QR format to start generating."
                                    items={QR_TOOLS}
                                />
                            }
                        />
                        <Route
                            path="/barcode"
                            element={
                                <ToolsGroupPage
                                    title="Barcode Generator Tools"
                                    description="Pick a 1D barcode format to start generating."
                                    items={BARCODE_TOOLS}
                                />
                            }
                        />
                        {QR_TOOLS.map((tool) => (
                            <Route
                                key={tool.id}
                                path={tool.path}
                                element={
                                    <QRToolPage key={tool.id} tool={tool} />
                                }
                            />
                        ))}
                        {BARCODE_TOOLS.map((tool) => (
                            <Route
                                key={tool.id}
                                path={tool.path}
                                element={
                                    <BarcodeToolPage
                                        key={tool.id}
                                        tool={tool}
                                    />
                                }
                            />
                        ))}
                        <Route path="*" element={<ToolsHome />} />
                    </Routes>
                </AppLayout>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
